"use client"

import type React from "react"
import { useState, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { FolderOpenIcon, UploadIcon, ImageDownIcon, Camera } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import LogoHeader from "./functions/header/logo-header"
import FundingFooter from "./functions/footer/funding-footer"
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion"

interface HomeScreenProps {
  onFileUpload: (file: File) => void
  onCameraCapture: () => void
  onError: (message: string) => void
  onNavigateToHome: () => void
}

export default function HomeScreen({ onFileUpload, onCameraCapture, onError, onNavigateToHome }: HomeScreenProps) {
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isTipsExpanded, setIsTipsExpanded] = useState(false) // State to track accordion expansion

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)

    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      handleFileSelection(files[0])
    }
  }

  const handleFileSelection = (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      onError("File size too large. Please upload a file smaller than 10MB.")
      return
    }

    const isImage = file.type.includes("image")
    const isPdf = file.type.includes("pdf")
    const isDocx =
      file.name.toLowerCase().endsWith(".docx") ||
      file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"

    if (!isImage && !isPdf && !isDocx) {
      onError("Unsupported file format. Please upload an image, PDF, or Word (.docx) file.")
      return
    }

    onFileUpload(file)
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleFileSelection(files[0])
    }
  }

  const handleCameraClick = async () => {
    try {
      // Request camera permission and get the stream
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      
      // IMPORTANT: Stop the stream before navigating to camera screen
      // This releases the camera so camera-screen can acquire it
      stream.getTracks().forEach(track => track.stop())
      
      // Small delay to ensure the camera is fully released before navigating
      await new Promise(resolve => setTimeout(resolve, 100))
      
      onCameraCapture()
    } catch (error) {
      onError("Camera access is not available on this device.")
    }
  }

  return (
    <div className="min-h-screen bg-[#FBF8FF] flex flex-col">
      {/*
        * No title, so the mobile header shows the logo - the same thing the
        * desktop one shows, and the same thing "Wgraj tekst" now says once,
        * below, instead of twice in two different places.
        *
        * LogoHeader's title is not dead: the camera, photo and PDF screens still
        * pass one ("Aparat", "Zdjęcie", "PDF"). The difference is what the header
        * is for on each. Those are sub-screens with a back button, where the
        * title says where you are; this is the start page, where there is nowhere
        * to go back to and the header is branding.
        */}
      <LogoHeader onLogoClick={onNavigateToHome} />

      {/* Main Content */}
      <main className="flex-1 flex justify-center p-4 bg-[rgba(251,248,255,1)] pt-[30px] md:pt-[150px] items-start">
        <div className="w-full max-w-2xl space-y-6">
          {/*
            * Shown at every width now. It used to be desktop-only, with the
            * phone getting the same words in the purple bar instead - so the two
            * layouts disagreed about what the bar is for, and the phone lost the
            * logo to a heading that reads better as a heading.
            *
            * The arrow scales with the line rather than keeping the 60px it was
            * given for the desktop size: beside text-3xl on a 390px screen that
            * was taller than the words it points at. Its alt was "Dyslexa",
            * which is the logo's name, not this - a screen reader announced the
            * brand in the middle of the heading. It is decorative, so it says
            * nothing now.
            */}
          <div className="text-center space-y-4">
            <h1 className="text-3xl font-bold sm:text-4xl text-[rgba(30,49,68,1)] flex justify-center gap-3 items-start">
              Wgraj tekst
              <Image
                src="/images/design-mode/arrow.png"
                alt=""
                aria-hidden="true"
                width={120}
                height={32}
                className="w-auto leading-7 h-[40px] md:h-[60px]"
              />
            </h1>
          </div>

          {/* Action Buttons */}
          <div className="grid gap-4">
            <Card className="transition-shadow border border-white border-none">
              <CardContent className="p-6 shadow-none">
                <div className="flex flex-col items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center rounded-full size-[60px] bg-transparent flex-shrink-0">
                      <ImageDownIcon className="size-10 text-[rgba(78,85,98,1)]" />
                    </div>
                    <h3 className="font-semibold text-xl text-[rgba(30,49,68,1)]">Użyj aparatu, aby dodać zdjęcie.</h3>
                  </div>

                  <Button
                    onClick={handleCameraClick}
                    className="hover:bg-primary/90 text-primary-foreground rounded-full bg-[rgba(85,54,109,1)] w-[250px]"
                    size="lg"
                  >
                    <Camera className="size-6 mr-1" />
                    Otwórz aparat
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Upload Area */}
            <Card
              className={`transition-colors border-0 border-[rgba(236,236,239,1)] border-none ${
                isDragOver ? "border-ring bg-accent" : "border-border"
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <CardContent className="p-6 shadow-none">
                <div className="flex flex-col items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center rounded-full size-[60px] bg-transparent flex-shrink-0">
                      <UploadIcon className="size-10 text-[rgba(78,85,98,1)]" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">Prześlij JPG, PNG, PDF lub Word (do 10 MB)</h3>
                  </div>

                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    className="hover:bg-primary/90 text-primary-foreground rounded-full bg-[rgba(85,54,109,1)] w-[250px]"
                    size="lg"
                  >
                    <FolderOpenIcon className="size-6 mr-1" />
                    Wybierz plik
                  </Button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleFileInputChange}
                    className="hidden"
                    aria-label="Upload file"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Tips */}
          <Accordion type="single" collapsible onValueChange={(value) => setIsTipsExpanded(value === "item-1")}>
            <AccordionItem value="item-1" className="border-none">
              <AccordionTrigger className="rounded-lg px-4 text-left hover:no-underline flex flex-row-reverse justify-end gap-x-2 bg-transparent py-0 mb-4">
                <h4 className="font-semibold text-foreground text-left">
                  {isTipsExpanded ? "Ukryj wskazówki" : "Pokaż wskazówki"}
                </h4>
              </AccordionTrigger>
              <AccordionContent className="rounded-lg p-6 bg-transparent pt-0">
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start space-x-2 mx-2">
                    <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 bg-muted-foreground"></div>
                    <span className="text-base">
                      Upewnij się, że tekst na zdjęciach jest wyraźny i dobrze oświetlony
                    </span>
                  </li>
                  <li className="flex items-start space-x-2 mx-2">
                    <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 bg-muted-foreground"></div>
                    <span className="text-base">Używaj obrazów o wysokim kontraście dla lepszego rozpoznawania</span>
                  </li>
                  <li className="flex items-start space-x-2 mx-2">
                    <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 bg-muted-foreground"></div>
                    <span className="text-base">Pliki PDF z zaznaczalnym tekstem działają najlepiej</span>
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

        </div>
      </main>

      {/* EU funding and Creative Commons marking (DYS-048). Start page only.
          <main> above already carries flex-1 inside this flex-col wrapper, so this sits at
          the bottom on a short page and is pushed down by the tips accordion on a tall one,
          without needing mt-auto or absolute positioning. */}
      <FundingFooter />
    </div>
  )
}
